/**
 * Sequence-diagram lifeline node.
 *
 * A thin (LIFE_W) node carrying: a header (actor stick-figure or participant box,
 * rendered centered over the line with overflow visible), a dashed vertical
 * lifeline spanning the diagram height, activation bars for each activation span,
 * and per-row connection handles (ids from the shared `sequenceRowHandleId`
 * keystone) so message edges can never reference a missing handle.
 */

import { memo, useEffect } from 'react'
import { NodeProps, Handle, Position, useUpdateNodeInternals } from '@xyflow/react'
import type { SequenceContent } from '../../types/schema'
import { sequenceRowHandleId } from '../../utils/sequenceHandles'
import { SEQ_LIFE_W, SEQ_HEADER_H, seqRowY } from '../../utils/sequenceLayout'

interface LifelineData {
  label: string
  sequenceContent?: SequenceContent
}

function SequenceLifelineNode({ id, data, height }: NodeProps) {
  const nodeData = data as unknown as LifelineData
  const sc = nodeData.sequenceContent
  const updateNodeInternals = useUpdateNodeInternals()
  const rowCount = sc?.rowCount ?? 0
  useEffect(() => { updateNodeInternals(id) }, [id, rowCount, updateNodeInternals])

  const totalH = typeof height === 'number' && height > SEQ_HEADER_H ? height : SEQ_HEADER_H + rowCount * 46 + 28
  const isActor = sc?.kind === 'actor'
  const name = sc?.participantName ?? nodeData.label

  const handleStyle = {
    width: 7, height: 7, minWidth: 0, minHeight: 0,
    background: '#cbd5e1', border: 'none', opacity: 0.01,
  } as const

  return (
    <div style={{ width: SEQ_LIFE_W, height: totalH, position: 'relative', overflow: 'visible' }}>
      {/* Header (overflows the thin node, centered over the lifeline) */}
      <div
        style={{
          position: 'absolute', top: 0, left: SEQ_LIFE_W / 2, transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 150,
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        {isActor ? (
          <svg width="30" height="40" viewBox="0 0 30 40" aria-hidden>
            <circle cx="15" cy="7" r="6" fill="none" stroke="#334155" strokeWidth="2" />
            <line x1="15" y1="13" x2="15" y2="27" stroke="#334155" strokeWidth="2" />
            <line x1="5" y1="18" x2="25" y2="18" stroke="#334155" strokeWidth="2" />
            <line x1="15" y1="27" x2="6" y2="38" stroke="#334155" strokeWidth="2" />
            <line x1="15" y1="27" x2="24" y2="38" stroke="#334155" strokeWidth="2" />
          </svg>
        ) : (
          <div
            style={{
              padding: '8px 14px', background: '#eef2ff', border: '1.5px solid #c7d2fe',
              borderRadius: 8, color: '#3730a3', fontWeight: 700, fontSize: 13,
              whiteSpace: 'nowrap', boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            }}
          >
            {name}
          </div>
        )}
        {isActor && (
          <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>{name}</div>
        )}
      </div>

      {/* Dashed lifeline */}
      <div
        style={{
          position: 'absolute', left: SEQ_LIFE_W / 2 - 1, top: SEQ_HEADER_H - 8,
          width: 0, height: Math.max(totalH - SEQ_HEADER_H, 0),
          borderLeft: '1.5px dashed #94a3b8',
        }}
      />

      {/* Activation bars */}
      {(sc?.activations ?? []).map((a, i) => {
        const top = seqRowY(a.startRow)
        const h = Math.max(seqRowY(a.endRow) - top, 12)
        const off = (a.depth ?? 0) * 4
        return (
          <div
            key={i}
            style={{
              position: 'absolute', left: SEQ_LIFE_W / 2 - 5 + off, top, width: 10, height: h,
              background: '#e2e8f0', border: '1px solid #94a3b8', borderRadius: 2,
            }}
          />
        )
      })}

      {/* Per-row connection handles (both sides, source + target) */}
      {Array.from({ length: rowCount }, (_, r) =>
        (['left', 'right'] as const).map((side) => {
          const hid = sequenceRowHandleId(r, side)
          const pos = side === 'left' ? Position.Left : Position.Right
          const style = { ...handleStyle, top: seqRowY(r) }
          return (
            <span key={hid}>
              <Handle type="source" id={hid} position={pos} style={style} isConnectable={false} />
              <Handle type="target" id={hid} position={pos} style={style} isConnectable={false} />
            </span>
          )
        })
      )}
    </div>
  )
}

export default memo(SequenceLifelineNode)
